"use client";

import { AnimatedFeatures } from "@/components/global-ui/animated-feature";
import { useMediaQuery } from "usehooks-ts";
interface FeatureConfig {
  nameIndex: number;
  descriptionIndex: number;
  videoIndex?: number;
  bannerImageIndex?: number;
}
interface Stage3FeaturesProps {
  titleIndex?: number;
  descriptionIndex?: number;
  subtitleIndex?: number;
  features?: FeatureConfig[];
  buttonText?: string;
  buttonLink?: string;
}
export function Stage3Features({
  titleIndex = 2,
  descriptionIndex = 3,
  subtitleIndex = 4,
  features = [{
    nameIndex: 4,
    descriptionIndex: 8,
    videoIndex: 1,
    bannerImageIndex: 6
  },
  // Launch Pad with launch pad demo
  {
    nameIndex: 5,
    descriptionIndex: 15,
    videoIndex: 2,
    bannerImageIndex: 9
  },
  // Viral Giveaways with viral giveaway demo
  {
    nameIndex: 6,
    descriptionIndex: 19,
    videoIndex: 3,
    bannerImageIndex: 7
  },
  // Promo Video Maker with promo video demo
  {
    nameIndex: 10,
    descriptionIndex: 24,
    videoIndex: 1,
    bannerImageIndex: 12
  } // Focus Task Manager with launch pad demo
  ],
  buttonText = "Start for $0",
  buttonLink = "/signup"
}: Stage3FeaturesProps) {
  // Check if device is large (lg breakpoint = 1024px and above)
  const isLargeDevice = useMediaQuery("(min-width: 1024px)");

  // Available titles
  const titles: {
    [key: number]: string;
  } = {
    1: "Stage 3: Scale (Turn Launch Day Into Payday)",
    2: "Stage 3: Growth systems & viral reach",
    3: "Phase 3: Go Viral (For Real)",
    4: "Phase 3: From Launch to Viral"
  };

  // Available descriptions
  const descriptions: {
    [key: number]: string;
  } = {
    1: "Stop launching into the void. Execute a coordinated launch across platforms where your customers actually spend time - with viral mechanics that multiply your reach.",
    2: "Launch systematically across 100+ platforms with viral amplification.",
    3: "Launch where your customers are. Viral Launch gives you a curated list of upto 100+ launch platforms to launch on. With tools like promo video maker and viral giveaway campaigns, you'll be ready to scale from day 1.",
    4: "You've got the foundation and content. Now it's time to get in front of people who will actually buy.",
    5: "This is where it clicks. Get seen, shared, and signed up for. Stop launching into the void. Launch where your customers are."
  };

  // Available subtitles/CTAs
  const subtitles: {
    [key: number]: string;
  } = {
    1: "10x your launch, not your stress. Get seen. Get shared. Get users.",
    2: "Traction on autopilot. Tools that build buzz without burning you out.",
    3: "Replace $2K–$30K/mo in launch costs. You don't need an agency. You need a system. This is that system.",
    4: "Traction on autopilot. You don't need an agency. You need a system. This is that system."
  };

  // Available feature names
  const featureNames: {
    [key: number]: string;
  } = {
    1: "Follow Your Launch Roadmap",
    2: "North Star",
    3: "Focus Task Manager",
    4: "Launch Pad",
    5: "Viral Giveaways",
    6: "Promo Video Maker",
    7: "10x your launch, not your stress",
    8: "Traction on autopilot",
    9: "Replace $2K–$30K/mo in launch costs",
    10: "Viral Launch"
  };

  // Available feature descriptions
  const featureDescriptions: {
    [key: number]: string;
  } = {
    // Task Management descriptions (1-6)
    1: "Stay focused on high-impact tasks with built in task-manager - eliminating overwhelm and keeping momentum.",
    2: "Track every launch task without getting overwhelmed. Kanban board view for planning, North Star banner for focus. See only your next task to maintain momentum.",
    3: "Stay focused on what moves the needle. Import your launch checklist, then use North Star mode to see only your next most important task.",
    4: "Focus on one high-leverage task at a time. Get out of your own way.",
    5: "Stay locked in on what moves the needle.",
    6: "Know what to do next. Stay focused. Ship faster.",
    // Launch Pad descriptions (7-11)
    7: "Access 100+ curated platforms where successful startups get their first customers. Import directly to your task manager and work through systematically.",
    8: "Curated list of upto 100+ launch platforms where successful startups get their first customers.",
    9: "A launch checklist of upto 100+ high-signal platforms — vetted and ranked.",
    10: "Go live on 100+ platforms that founders actually use.",
    11: "35 to 100+ top-tier places to drop your product — from Product Hunt to niche Slack groups.",
    // Viral Giveaways descriptions (12-17)
    12: "Turn visitors into brand ambassadors. Create referral-powered campaigns that make your audience do the marketing for you - exponential growth, automated.",
    13: "Referral-powered campaigns that grow themselves. Set up tasks (follow, like, sign up), track entries automatically, and watch participants recruit their friends.",
    14: "Set up referral-powered giveaways that make your audience do the marketing for you. Watch sign-ups multiply exponentially.",
    15: "Turn one customer into ten through referrals. Create viral giveaways that reward people for bringing friends.",
    16: "Turn visitors into brand ambassadors. Create giveaway campaigns where participants complete tasks (follow, share, sign up) to earn entries.",
    17: "Built-in growth loop: get users to share, refer, and hype your launch.",
    // Promo Video Maker descriptions (18-20)
    18: "Create scroll-stopping videos in minutes. Use our browser-based editor to create professional demo videos, feature highlights, and promotional content.",
    19: "Tired of sharing those boring screenshots? Use the promo video templates to create videos that get attention and drive clicks.",
    20: "Easy-to-setup promo video templates for your launch post or demo page. Videos > stills",
    // CTA section (21-23)
    21: "10x your launch, not your stress. Get seen. Get shared. Get users.",
    22: "Traction on autopilot. Tools that build buzz without burning you out.",
    23: "Replace $2K–$30K/mo in launch costs. You don't need an agency. You need a system. This is that system.",
    24: "Give your startup the best chance of success. Launch on 100+ platforms, with virality baked in."
  };

  // Available video URLs
  const videoUrls: {
    [key: number]: string;
  } = {
    1: isLargeDevice ? "https://viral.devassets.app/hls_launchpad/desktop.m3u8" : "https://viral.devassets.app/hls_launchpad/master.m3u8",
    2: isLargeDevice ? "https://viral.devassets.app/hls_viral_giveaway/desktop.m3u8" : "https://viral.devassets.app/hls_viral_giveaway/master.m3u8",
    3: isLargeDevice ? "https://viral.devassets.app/hls_promo_video/desktop.m3u8" : "https://viral.devassets.app/hls_promo_video/master.m3u8",
    4: isLargeDevice ? "https://viral.devassets.app/hls_launchpad/desktop.m3u8" : "https://viral.devassets.app/hls_launchpad/master.m3u8"
  };

  // Available still image URLs for video placeholders
  const stillImageUrls: {
    [key: number]: string;
  } = {
    1: "https://viral.devassets.app/stills/viral_launchpad_still.png",
    2: "https://viral.devassets.app/stills/viral_giveaway_still.png",
    3: "https://viral.devassets.app/stills/viral_promo_video_still.png",
    4: "https://viral.devassets.app/stills/viral_launchpad_still.png" // Reusing launchpad still for 4th feature
  };

  // Available banner image URLs from demos folder
  const bannerImageUrls: {
    [key: number]: string;
  } = {
    1: "/images/demos/viral-name-demo.png",
    2: "/images/demos/logo-generator-demo.png",
    3: "/images/demos/persona-generator-demo.png",
    4: "/images/demos/brand-voice-extractor-demo.png",
    5: "/images/demos/content-hub-demo.png",
    6: "/images/demos/launch-pad-demo.png",
    7: "/images/demos/promo-video-demo.png",
    8: "/images/demos/smart-edit-demo.png",
    9: "/images/demos/viral-giveaway-demo.png",
    10: "/images/demos/section-1.png",
    11: "/images/demos/section-2.png",
    12: "/images/demos/section-3.png"
  };

  // Build features array based on configuration
  const configuredFeatures = features.map((featureConfig, index) => ({
    id: index + 1,
    name: featureNames[featureConfig.nameIndex] || `Feature ${featureConfig.nameIndex}`,
    description: featureDescriptions[featureConfig.descriptionIndex] || `Description ${featureConfig.descriptionIndex}`,
    video: videoUrls[featureConfig.videoIndex || 1],
    stillImage: stillImageUrls[featureConfig.videoIndex || 1],
    bannerImage: bannerImageUrls[featureConfig.bannerImageIndex || 1],
    // Add button to last feature
    ...(index === features.length - 1 && {
      buttonText,
      buttonLink
    })
  }));
  return <AnimatedFeatures title={titles[titleIndex] || titles[1]} description={descriptions[descriptionIndex] || descriptions[1]} subtitle={subtitles[subtitleIndex] || subtitles[1]} features={configuredFeatures} autoRotateInterval={12000} // Increased from 6000 to reduce memory pressure
  data-sentry-element="AnimatedFeatures" data-sentry-component="Stage3Features" data-sentry-source-file="stage-3-features.tsx" />;
}